import { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, ExternalLink, Eye, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { listCustomers } from '../api/customers';
import {
  getMercadoPagoSettings,
  listPayments
} from '../api/payments';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import { currency, dateTime } from '../utils/format';
import { getStatusConfig, paymentStatus } from '../utils/status';

const Pagos = () => {
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const [origin, setOrigin] = useState('');
  const [receipt, setReceipt] = useState('');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      const [paymentData, customerData, mercadoPagoData] = await Promise.all([
        listPayments({
          customer_id: customerId || undefined,
          method: method || undefined,
          status: status || undefined,
          origin: origin || undefined,
          receipt_number: receipt || undefined,
          limit: 100
        }),
        listCustomers({ limit: 100 }),
        getMercadoPagoSettings().catch(() => null)
      ]);
      setPayments(paymentData);
      setCustomers(customerData);
      setSettings(mercadoPagoData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los pagos'));
    } finally {
      setLoading(false);
    }
  }, [customerId, method, origin, receipt, status]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const customerById = useMemo(() => new Map(customers.map((customer) => [Number(customer.id), customer])), [customers]);
  const total = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const totalDiscount = payments.reduce((sum, payment) => sum + Number(payment.discount_amount || 0), 0);
  const mercadoPagoReady = settings?.integration_model === 'split_1_1'
    ? Boolean(settings?.split_ready)
    : Boolean(settings?.is_active);

  const columns = [
    {
      key: 'receipt_number',
      label: 'Boleta',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-900">{row.receipt_number || `#${row.id}`}</p>
          <p className="text-xs text-gray-500">{row.title || row.target_type || 'Pago'}</p>
        </div>
      )
    },
    {
      key: 'customer_id',
      label: 'Cliente',
      render: (row) => row.customer_name || customerById.get(Number(row.customer_id))?.full_name || `#${row.customer_id}`
    },
    { key: 'target_type', label: 'Origen', render: (row) => row.target_type || row.source || '-' },
    {
      key: 'amount',
      label: 'Monto',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-900">{currency(row.amount)}</p>
          {Number(row.discount_amount || 0) > 0 && (
            <p className="text-xs text-green-700">
              -{currency(row.discount_amount)} descuento
            </p>
          )}
        </div>
      )
    },
    { key: 'method', label: 'Metodo', render: (row) => row.method || '-' },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const config = getStatusConfig(paymentStatus, row.status);
        return <StatusBadge label={config.label} color={config.color} />;
      }
    },
    { key: 'created_at', label: 'Creado', render: (row) => dateTime(row.created_at) },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedPayment(row)}
            className="rounded-md p-2 text-gray-700 hover:bg-gray-100"
            title="Ver detalle"
          >
            <Eye className="h-4 w-4" />
          </button>
          {row.checkout_url && (
            <a
              href={row.checkout_url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md p-2 text-green-700 hover:bg-green-50"
              title="Abrir checkout"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pagos"
        description="Pagos, intenciones Mercado Pago, conciliacion y boletas."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <CreditCard className="h-5 w-5 text-blue-600" />
          <p className="mt-2 text-sm text-gray-500">Total listado</p>
          <p className="text-2xl font-bold text-gray-950">{currency(total)}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Transacciones</p>
          <p className="mt-2 text-2xl font-bold text-gray-950">{payments.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Mercado Pago</p>
          <p className="mt-2 text-2xl font-bold text-gray-950">{mercadoPagoReady ? 'Activo' : 'Pendiente'}</p>
          <p className="mt-1 text-xs text-gray-500">{settings?.integration_model === 'split_1_1' ? 'Split 1:1 Checkout API' : 'Directo'}</p>
          {settings?.integration_model === 'split_1_1' && (
            <p className="mt-1 text-xs text-gray-500">
              Vendedor OAuth: {settings?.split_ready ? 'configurado' : 'pendiente'}
            </p>
          )}
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 md:col-span-3">
          <p className="text-sm text-green-800">Descuentos aplicados en el listado</p>
          <p className="mt-1 text-2xl font-bold text-green-950">{currency(totalDiscount)}</p>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-6">
          <FormField label="Cliente">
            <select className={inputClassName} value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
              <option value="">Todos</option>
              {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.full_name}</option>)}
            </select>
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="paid">Pagado</option>
              <option value="failed">Fallido</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </FormField>
          <FormField label="Origen">
            <select className={inputClassName} value={origin} onChange={(event) => setOrigin(event.target.value)}>
              <option value="">Todos</option>
              <option value="appointment">Cita</option>
              <option value="order">Pedido</option>
              <option value="direct_charge">Cobro directo</option>
              <option value="intent">Intención</option>
              <option value="legacy">Conciliado</option>
            </select>
          </FormField>
          <FormField label="Metodo">
            <select className={inputClassName} value={method} onChange={(event) => setMethod(event.target.value)}>
              <option value="">Todos</option>
              <option value="mercado_pago">Mercado Pago</option>
              <option value="cash">Efectivo</option>
              <option value="card">Tarjeta</option>
              <option value="apple_pay">Apple Pay</option>
            </select>
          </FormField>
          <FormField label="Boleta">
            <input
              className={inputClassName}
              value={receipt}
              onChange={(event) => setReceipt(event.target.value)}
              placeholder="VS-..."
            />
          </FormField>
          <div className="flex items-end">
            <Button icon={Search} variant="secondary" onClick={loadPayments} className="w-full">Actualizar</Button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="p-5"><LoadingState rows={5} /></div>
        ) : (
          <Table columns={columns} data={payments} emptyMessage="No hay pagos para los filtros seleccionados" />
        )}
      </section>

      <Modal
        open={Boolean(selectedPayment)}
        title="Detalle de pago"
        onClose={() => setSelectedPayment(null)}
        footer={<Button variant="secondary" onClick={() => setSelectedPayment(null)}>Cerrar</Button>}
      >
        {selectedPayment && (
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <p><span className="font-semibold">Boleta:</span> {selectedPayment.receipt_number}</p>
            <p><span className="font-semibold">Estado:</span> {selectedPayment.status}</p>
            <p><span className="font-semibold">Cliente:</span> {selectedPayment.customer_name || selectedPayment.customer_id}</p>
            {Number(selectedPayment.discount_amount || 0) > 0 && (
              <>
                <p><span className="font-semibold">Monto original:</span> {currency(selectedPayment.original_amount)}</p>
                <p><span className="font-semibold">Descuento:</span> -{currency(selectedPayment.discount_amount)}</p>
                <p><span className="font-semibold">Beneficio:</span> {selectedPayment.discount?.name || selectedPayment.discount?.code || '-'}</p>
              </>
            )}
            <p><span className="font-semibold">Monto:</span> {currency(selectedPayment.amount)}</p>
            <p><span className="font-semibold">Origen:</span> {selectedPayment.target_type || selectedPayment.source}</p>
            <p><span className="font-semibold">Metodo:</span> {selectedPayment.method || '-'}</p>
            <p><span className="font-semibold">Mercado Pago:</span> {selectedPayment.mercado_pago_payment_id || selectedPayment.preference_id || '-'}</p>
            <p><span className="font-semibold">Pagado:</span> {dateTime(selectedPayment.paid_at)}</p>
            {selectedPayment.checkout_url && (
              <div className="md:col-span-2">
                <a className="text-blue-700 hover:underline" href={selectedPayment.checkout_url} target="_blank" rel="noreferrer">
                  Abrir checkout
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Pagos;
